import {
  faTimes,
  faPlus,
  faFile,
  faEllipsisVertical,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  ColorInput,
  Googlefontloader,
  Modal,
  Popover,
  PopoverAnchor,
  PopoverContent,
  SelectInput,
  TextareaInput,
  TextInput,
  ToggleInput,
  UnitInput,
} from "../../input";
import CustomFontLoader from "../../input/customfontloader";
import { generateKey } from "../../input/generateID";
import { GrigoraEditorContext } from "../context";
import slugify from "slugify";
import { makeID } from "../../helpers";
import { motion } from "framer-motion";
import GoogleFonts from "./../../input/gfonts.json";
import EmptyBody from "./empty-body";
import { ModalInput } from "../../ui-kit";

const TYPOGRAPHY_SETS = {
  body: {
    label: "Body",
    familySource: ["body"],
  },
  "site-title": {
    label: "Site Title",
    familySource: ["site-title"],
  },
  h1: {
    label: "Heading 1",
    default: {
      size: "2.625rem",
      lineHeight: "1.2em",
      weight: "700",
    },
    familySource: ["h1", "h*", "body"],
  },
  h2: {
    label: "Heading 2",
    default: {
      size: "2.25rem",
      lineHeight: "1.2em",
      weight: "700",
    },
    familySource: ["h2", "h*", "body"],
  },
  h3: {
    label: "Heading 3",
    default: {
      size: "1.75rem",
      weight: "700",
    },
    familySource: ["h3", "h*", "body"],
  },
  h4: {
    label: "Heading 4",
    default: {
      size: "1.5rem",
      weight: "700",
    },
    familySource: ["h4", "h*", "body"],
  },
  h5: {
    label: "Heading 5",
    default: {
      size: "1.25rem",
      weight: "700",
    },
    familySource: ["h5", "h*", "body"],
  },
  h6: {
    label: "Heading 6",
    default: {
      size: "1.125rem",
      weight: "700",
    },
    familySource: ["h6", "h*", "body"],
  },
};

const EditTypography = (props) => {
  const { type } = props;

  const [editPageModal, setEditPageModal] = useState(false);

  const { project, setProjectData } = useContext(GrigoraEditorContext);

  const { typography } = project || {};

  const [internalTypography, setInternalTypography] = useState(typography);
  const [internalTypographyFamily, setInternalTypographyFamily] = useState(
    project?.fonts?.[type]?.family,
  );

  useEffect(() => {
    setInternalTypography(typography);
  }, [typography]);

  const changeTypography = (propKey, prop, val) => {
    const newTypography = {
      ...(internalTypography ? internalTypography : {}),
      [propKey]: {
        ...(internalTypography?.[propKey] ? internalTypography[propKey] : {}),
        [prop]: val,
      },
    };
    setInternalTypography(newTypography);
  };

  return (
    <>
      <CustomFontLoader context={window} />
      <Googlefontloader
        config={{
          google: {
            families: [internalTypographyFamily],
          },
        }}
        context={window}
      />
      <div
        onClick={() => {
          setEditPageModal(true);
        }}
        className={`grigora-page-tile border border-transparent hover:border-ui-dark-blue duration-200 page-${type} bg-white cursor-pointer relative px-4 py-3 pr-0 rounded-[8px] mb-2 flex gap-2 items-center text-grigora-base`}
      >
        <span
          className="text-ellipsis whitespace-nowrap truncate"
          title={TYPOGRAPHY_SETS?.[type]?.label}
          style={{
            fontFamily: internalTypographyFamily,
            fontSize:
              project?.typography?.[type]?.size ??
              TYPOGRAPHY_SETS?.[type]?.default?.size,
            lineHeight:
              project?.typography?.[type]?.lineHeight ??
              TYPOGRAPHY_SETS?.[type]?.default?.lineHeight,
            letterSpacing:
              project?.typography?.[type]?.letterSpacing ??
              TYPOGRAPHY_SETS?.[type]?.default?.letterSpacing,
            wordSpacing:
              project?.typography?.[type]?.wordSpacing ??
              TYPOGRAPHY_SETS?.[type]?.default?.wordSpacing,
            fontWeight:
              project?.typography?.[type]?.weight ??
              TYPOGRAPHY_SETS?.[type]?.default?.weight,
            textTransform:
              project?.typography?.[type]?.transform ??
              TYPOGRAPHY_SETS?.[type]?.default?.transform,
            textDecoration:
              project?.typography?.[type]?.decoration ??
              TYPOGRAPHY_SETS?.[type]?.default?.decoration,
            fontStyle:
              project?.typography?.[type]?.style ??
              TYPOGRAPHY_SETS?.[type]?.default?.style,
            color:
              project?.typography?.[type]?.color ??
              TYPOGRAPHY_SETS?.[type]?.default?.color,
          }}
        >
          {TYPOGRAPHY_SETS?.[type]?.label}
        </span>
      </div>
      <ModalInput
        isOpened={editPageModal}
        onOutsideClick={(e) => {
          setEditPageModal(false);
        }}
      >
        <div className="w-[80vw] h-[80vh] p-4 flex">
          <div className="flex flex-1 w-1/2 flex-col p-4">
            <div className="modal-heading mb-3">
              Edit {TYPOGRAPHY_SETS?.[type]?.label}
            </div>
            <SelectInput
              isResetVisible={false}
              value={internalTypographyFamily}
              onChange={(val) => {
                setInternalTypographyFamily(val);
              }}
              options={[{ label: "-", value: "" }]
                .concat(
                  Object.keys(GoogleFonts).map((item) => {
                    return {
                      label: item,
                      value: item,
                    };
                  }),
                )
                ?.concat(
                  project?.customFonts?.map((item) => {
                    return {
                      label: item.family,
                      value: item.family,
                    };
                  }) ?? [],
                )}
              label="Family"
            />
            <UnitInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.size ??
                TYPOGRAPHY_SETS?.[type]?.default?.size
              }
              onChange={(val) => {
                changeTypography(type, "size", val);
              }}
              label={"Size"}
            />
            <UnitInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.lineHeight ??
                TYPOGRAPHY_SETS?.[type]?.default?.lineHeight
              }
              onChange={(val) => {
                changeTypography(type, "lineHeight", val);
              }}
              label={"Line Height"}
            />
            <UnitInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.letterSpacing ??
                TYPOGRAPHY_SETS?.[type]?.default?.letterSpacing
              }
              onChange={(val) => {
                changeTypography(type, "letterSpacing", val);
              }}
              label={"Letter Spacing"}
            />
            <UnitInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.wordSpacing ??
                TYPOGRAPHY_SETS?.[type]?.default?.wordSpacing
              }
              onChange={(val) => {
                changeTypography(type, "wordSpacing", val);
              }}
              label={"Word Spacing"}
            />
            <SelectInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.weight ??
                TYPOGRAPHY_SETS?.[type]?.default?.weight
              }
              onChange={(val) => {
                changeTypography(type, "weight", val);
              }}
              options={[
                { label: "100", value: "100" },
                { label: "200", value: "200" },
                { label: "300", value: "300" },
                { label: "400", value: "400" },
                { label: "500", value: "500" },
                { label: "600", value: "600" },
                { label: "700", value: "700" },
                { label: "800", value: "800" },
                { label: "900", value: "900" },
              ]}
              label="Weight"
            />
            <SelectInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.transform ??
                TYPOGRAPHY_SETS?.[type]?.default?.transform
              }
              onChange={(val) => {
                changeTypography(type, "transform", val);
              }}
              options={[
                { label: "None", value: "" },
                { label: "Uppercase", value: "uppercase" },
                { label: "Lowercase", value: "lowercase" },
                { label: "Capitalize", value: "capitalize" },
              ]}
              label="Transform"
            />
            <SelectInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.decoration ??
                TYPOGRAPHY_SETS?.[type]?.default?.decoration
              }
              onChange={(val) => {
                changeTypography(type, "decoration", val);
              }}
              options={[
                { label: "None", value: "" },
                { label: "Underline", value: "underline" },
                { label: "Overline", value: "overline" },
              ]}
              label="Decoration"
            />
            <SelectInput
              isResetVisible={false}
              value={
                internalTypography?.[type]?.style ??
                TYPOGRAPHY_SETS?.[type]?.default?.style
              }
              onChange={(val) => {
                changeTypography(type, "style", val);
              }}
              options={[
                {
                  label: "Normal",
                  value: "normal",
                },
                {
                  label: "Italic",
                  value: "italic",
                },
              ]}
              label="Style"
            />
            <div className="text-grigora-base justify-end flex items-center">
              <ColorInput
                isResetVisible={false}
                value={
                  internalTypography?.[type]?.color ??
                  TYPOGRAPHY_SETS?.[type]?.default?.color
                }
                onChange={(val) => {
                  changeTypography(type, "color", val);
                }}
                label="Color"
                contentClassName={"!z-[10010]"}
                className={"w-1/2"}
              />
            </div>
            <div className="mt-auto justify-end flex gap-4 text-[14px]">
              {/* Update button */}
              <button
                onClick={() => {
                  setProjectData({
                    ...(project ? project : {}),
                    typography: internalTypography,
                    fonts: {
                      ...(project?.fonts ? project.fonts : {}),
                      [type]: {
                        family: internalTypographyFamily,
                      },
                    },
                  });
                  setEditPageModal(false);
                }}
                className="grigora-action-btn"
              >
                Update
              </button>
            </div>
          </div>
          <div className="flex flex-1 w-1/2 flex-col text-[14px] font-[300] gap-2">
            Preview
            <div className="flex flex-1 rounded-[7px] border border-gray-300 p-4 overflow-y-auto">
              <div
                className="duration-300"
                style={{
                  fontFamily: internalTypographyFamily,
                  fontSize:
                    internalTypography?.[type]?.size ??
                    TYPOGRAPHY_SETS?.[type]?.default?.size,
                  lineHeight:
                    internalTypography?.[type]?.lineHeight ??
                    TYPOGRAPHY_SETS?.[type]?.default?.lineHeight,
                  letterSpacing:
                    internalTypography?.[type]?.letterSpacing ??
                    TYPOGRAPHY_SETS?.[type]?.default?.letterSpacing,
                  wordSpacing:
                    internalTypography?.[type]?.wordSpacing ??
                    TYPOGRAPHY_SETS?.[type]?.default?.wordSpacing,
                  fontWeight:
                    internalTypography?.[type]?.weight ??
                    TYPOGRAPHY_SETS?.[type]?.default?.weight,
                  textTransform:
                    internalTypography?.[type]?.transform ??
                    TYPOGRAPHY_SETS?.[type]?.default?.transform,
                  textDecoration:
                    internalTypography?.[type]?.decoration ??
                    TYPOGRAPHY_SETS?.[type]?.default?.decoration,
                  fontStyle:
                    internalTypography?.[type]?.style ??
                    TYPOGRAPHY_SETS?.[type]?.default?.style,
                  color:
                    internalTypography?.[type]?.color ??
                    TYPOGRAPHY_SETS?.[type]?.default?.color,
                }}
              >
                {
                  // Switch between type
                  type === "body" ? (
                    <EmptyBody />
                  ) : type === "site-title" ? (
                    <>{project?.name}</>
                  ) : (
                    <>The only true wisdom is in knowing you know nothing.</>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </ModalInput>
    </>
  );
};

const Typography = () => {
  const { pagesData, setLeftWidget, project, addNewPage, projectComponents } =
    useContext(GrigoraEditorContext);

  function getWebfontFamilies() {
    if (project && project.fonts && typeof project.fonts === "object") {
      var fonts = [];
      Object.keys(project.fonts).map((font) => {
        let fontvars = [];
        fontvars.push(project.fonts[font].family);
        fontvars.push(
          GoogleFonts?.[project.fonts[font].family]?.variants?.join(","),
        );
        fontvars.push(
          GoogleFonts?.[project.fonts[font].family]?.subsets?.join(","),
        );
        fontvars = fontvars.filter((item) => item);
        fonts.push(fontvars.join(":"));
      });
      return fonts;
    }
    return [];
  }

  const webloaderFamilies = useMemo(
    () => getWebfontFamilies(),
    [project ? project.fonts : project],
  );

  return (
    <>
      <CustomFontLoader context={window} />
      <Googlefontloader
        config={{
          google: {
            families: webloaderFamilies,
          },
        }}
        context={window}
      />
      <motion.div
        initial={{ x: -400 }}
        animate={{ x: 0 }}
        exit={{ x: -400 }}
        transition={{ duration: 0.3, delay: 0 }}
        key="typography"
        style={{
          clipPath: "inset(0px -200vw -100vh 0px)",
        }}
        className="bg-white border-l border-t shadow-editor-floating-elements z-[-1] w-[400px] text scroll-auto select-none"
      >
        <div className="h-10 w-[400px] border-b flex px-5 items-center">
          <h4 className="text-[14px] w-full font-medium flex items-center justify-between">
            Typography
            <span
              onClick={() => {
                setLeftWidget("");
              }}
              className="float-right cursor-pointer duration-200 w-[25px] h-[25px] flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faTimes} />
            </span>
          </h4>
        </div>
        <div className="grigora-left-sidebar__pages p-2 text-xs">
          {Object.keys(TYPOGRAPHY_SETS).map((key) => {
            return (
              <>
                <EditTypography key={`typography-edit-${key}`} type={key} />
              </>
            );
          })}
        </div>
      </motion.div>
    </>
  );
};

export default Typography;
